// Sequential sweep scheduler. It walks the cells of a SweepSpec ONE AT A TIME,
// back to back, with a fixed quiet gap between cells so the network settles.
// There is deliberately no parallelism: a cell fully finishes before the next
// begins (hard constraint 1). Cells are grouped by node so the underlying peer
// connection can be reused across a node's cells; the runner is told when a node
// boundary is crossed so it can tear the previous connection down.

import { type CellResult, type Settings, type NodeInfo, type SweepResult, type SweepSpec } from './types';

/** Default quiet gap between cells, in milliseconds. */
export const DEFAULT_QUIET_GAP_MS = 500;

/** A planned sweep cell, before it is measured. */
export interface PlannedCell {
  nodeId: string;
  size: number;
  pps: number;
  repeat: number;
  /** Global 0-based position in the sweep. */
  index: number;
}

/** Runs a single sweep cell and manages per-node connection reuse. */
export interface SweepRunner {
  /** Measure one cell and return its run result. */
  runCell(args: {
    node: NodeInfo;
    size: number;
    pps: number;
    settings: Settings;
    index: number;
    total: number;
  }): Promise<CellResult['run']>;
  /** Optional: tear down the (reused) connection for a node when leaving it. */
  closeNode?(nodeId: string): void | Promise<void>;
}

/** Injectable dependencies for the sweep scheduler. */
export interface SweepDeps {
  runner: SweepRunner;
  /** Promise resolving after `ms`, cancellable via signal. */
  delay: (ms: number, signal?: { aborted: boolean }) => Promise<void>;
  /** Resolve a node id to its full info. */
  nodeInfo: (nodeId: string) => NodeInfo;
  /** Quiet gap between cells; defaults to DEFAULT_QUIET_GAP_MS. */
  quietGapMs?: number;
}

/** Progress callbacks for a sweep. */
export interface SweepHooks {
  onCellStart?: (c: PlannedCell & { total: number }) => void;
  onCell?: (cell: CellResult) => void;
}

/** Build the ordered (size, pps) grid for one node from a spec. */
export function buildGrid(
  sizes: readonly number[],
  ppsList: readonly number[],
  order: SweepSpec['order'],
): [number, number][] {
  const out: [number, number][] = [];
  if (order === 'pps-major') {
    for (const pps of ppsList) {
      for (const size of sizes) out.push([size, pps]);
    }
  } else {
    for (const size of sizes) {
      for (const pps of ppsList) out.push([size, pps]);
    }
  }
  return out;
}

/**
 * Expand a spec into the full, strictly-ordered list of cells. Nodes are the
 * outermost loop (so a node's cells are contiguous and its connection can be
 * reused), then the (size, pps) grid (or explicit pairs), then repeats
 * innermost so each cell's repeats run back to back.
 */
export function enumerateCells(spec: SweepSpec): PlannedCell[] {
  const grid: [number, number][] =
    spec.pairs && spec.pairs.length > 0
      ? spec.pairs.map(([s, p]) => [s, p] as [number, number])
      : buildGrid(spec.sizes, spec.pps, spec.order);
  const repeats = Math.max(1, spec.repeats);
  const cells: PlannedCell[] = [];
  let index = 0;
  for (const nodeId of spec.nodes) {
    for (const [size, pps] of grid) {
      for (let repeat = 0; repeat < repeats; repeat++) {
        cells.push({ nodeId, size, pps, repeat, index: index++ });
      }
    }
  }
  return cells;
}

/** Derive the per-cell Settings from a sweep spec. */
export function cellSettings(spec: SweepSpec, size: number, pps: number): Settings {
  return {
    sizes: [size],
    pps,
    durationMs: spec.durationMs,
    acceptableDelayMs: spec.acceptableDelayMs,
    ipMode: 'auto',
    warmupMs: spec.warmupMs,
  };
}

/**
 * Execute a sweep sequentially. Exactly one cell is in flight at any moment: the
 * next cell only starts after the previous one's promise resolves and the quiet
 * gap elapses. When a node boundary is crossed the previous node's connection is
 * closed before the gap.
 */
export async function runSweep(
  spec: SweepSpec,
  deps: SweepDeps,
  hooks: SweepHooks = {},
  signal?: { aborted: boolean },
): Promise<SweepResult> {
  const gap = deps.quietGapMs ?? DEFAULT_QUIET_GAP_MS;
  const cells = enumerateCells(spec);
  const results: CellResult[] = [];

  let prevNode: string | null = null;
  let first = true;

  for (const planned of cells) {
    if (signal?.aborted) break;

    if (prevNode !== null && prevNode !== planned.nodeId) {
      await deps.runner.closeNode?.(prevNode);
    }
    if (!first) {
      await deps.delay(gap, signal);
    }
    first = false;

    const node = deps.nodeInfo(planned.nodeId);
    const settings = cellSettings(spec, planned.size, planned.pps);
    hooks.onCellStart?.({ ...planned, total: cells.length });

    const run = await deps.runner.runCell({
      node,
      size: planned.size,
      pps: planned.pps,
      settings,
      index: planned.index,
      total: cells.length,
    });

    const cell: CellResult = {
      nodeId: planned.nodeId,
      size: planned.size,
      pps: planned.pps,
      repeat: planned.repeat,
      run,
    };
    results.push(cell);
    hooks.onCell?.(cell);
    prevNode = planned.nodeId;
  }

  if (prevNode !== null) {
    await deps.runner.closeNode?.(prevNode);
  }

  return { spec, cells: results };
}
